export const KUBERNETES_ID_ANNOTATION = 'backstage.io/kubernetes-id';

export interface ServiceAllocation {
  cpuCost: number;
  ramCost: number;
  pvCost: number;
  networkCost: number;
  loadBalancerCost: number;
  totalCost: number;
}

export interface EnvironmentAllocation {
  environment: string;
  allocation: ServiceAllocation;
}

export interface ClusterStatus {
  name: string;
  status: 'ok' | 'unreachable';
  asOf: string | null;
  error?: string;
}

export interface ServiceAllocationResponse {
  window: string;
  environments: EnvironmentAllocation[];
  clusters: ClusterStatus[];
}

const COST_KEYS: Array<keyof ServiceAllocation> = [
  'cpuCost',
  'ramCost',
  'pvCost',
  'networkCost',
  'loadBalancerCost',
  'totalCost',
];

export const ENVIRONMENT_ORDER = ['dev', 'staging', 'preprod', 'prod', 'mgmt'];

export function addAllocations(
  target: ServiceAllocation,
  source: Partial<ServiceAllocation>,
): ServiceAllocation {
  for (const key of COST_KEYS) {
    target[key] += source[key] ?? 0;
  }
  return target;
}

export function sortByEnvironment<T extends { environment: string }>(
  entries: T[],
): T[] {
  return entries.sort((a, b) => {
    const ai = ENVIRONMENT_ORDER.indexOf(a.environment);
    const bi = ENVIRONMENT_ORDER.indexOf(b.environment);
    if (ai === -1 && bi === -1) {
      return a.environment.localeCompare(b.environment);
    }
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

export function formatCost(value: number): string {
  const abs = Math.abs(value);
  if (abs > 0 && abs < 0.01) {
    return value > 0 ? '< $0.01' : '> -$0.01';
  }
  const sign = value < 0 ? '-' : '';
  return `${sign}$${abs.toFixed(2)}`;
}

/** A cluster the browser should caption: unreachable, whether or not the
 * backend still has older data to serve for it. */
export function staleClusters(clusters: ClusterStatus[]): ClusterStatus[] {
  return clusters.filter(cluster => cluster.status !== 'ok');
}

function isAllocation(value: unknown): value is ServiceAllocation {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return COST_KEYS.every(key => typeof record[key] === 'number');
}

export function isServiceAllocationResponse(
  value: unknown,
): value is ServiceAllocationResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const body = value as Record<string, unknown>;
  if (typeof body.window !== 'string') {
    return false;
  }
  if (!Array.isArray(body.environments) || !Array.isArray(body.clusters)) {
    return false;
  }
  return (
    body.environments.every(
      entry =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as Record<string, unknown>).environment === 'string' &&
        isAllocation((entry as Record<string, unknown>).allocation),
    ) &&
    body.clusters.every(entry => {
      if (typeof entry !== 'object' || entry === null) {
        return false;
      }
      const cluster = entry as Record<string, unknown>;
      return (
        typeof cluster.name === 'string' &&
        (cluster.status === 'ok' || cluster.status === 'unreachable') &&
        (cluster.asOf === null || typeof cluster.asOf === 'string')
      );
    })
  );
}
